import React from 'react';
import { useTheme } from '../hooks/useTheme';
import Button from './atoms/Button';

interface ThemeToggleProps {
  className?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  showLabel = false,
}) => {
  const {
    theme, resolvedTheme, setTheme, toggleTheme,
  } = useTheme();

  const handleThemeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTheme(event.target.value as 'light' | 'dark' | 'system');
  };

  // Simple toggle button (switches between light/dark)
  const ToggleButton = () => (
    <Button
      onClick={toggleTheme}
      title={`Switch to ${resolvedTheme === 'light' ? 'dark' : 'light'} theme`}
      aria-label={`Switch to ${resolvedTheme === 'light' ? 'dark' : 'light'} theme`}
      variant="secondary"
    >
      {resolvedTheme === 'light' ? (
        // Moon icon for switching to dark
        <svg
          className="w-5 h-5 mx-auto"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        // Sun icon for switching to light
        <svg
          className="w-5 h-5 mx-auto"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            // eslint-disable-next-line max-len
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </Button>
  );

  // Advanced selector (includes system option)
  const ThemeSelector = () => (
    <div className="flex items-center gap-2">
      <select
        value={theme}
        onChange={handleThemeChange}
        className="
          px-3 py-2
          bg-surface-base
          border border-border-primary
          rounded-lg
          text-text-primary
          text-sm
          focus:outline-none
          focus:ring-2
          focus:ring-primary-500
          focus:border-primary-500
        "
        aria-label="Select theme"
      >
        <option value="light">Light</option>
        <option value="dark">Dark</option>
        <option value="system">System</option>
      </select>

      <div className="text-xs text-text-secondary">
        {theme === 'system' && (
          <span className="italic">
            (using {resolvedTheme})
          </span>
        )}
      </div>
    </div>
  );

  if (showLabel) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <span className="text-sm font-medium text-text-primary">
          Theme
        </span>
        <ThemeSelector />
      </div>
    );
  }

  return <ToggleButton />;
};

export default ThemeToggle;
